const axios = require('axios');
const assign = require('./utils/assign');

module.exports = {
  /**
   * Sends a XHR request with provided options
   * @see https://github.com/axios/axios
   */
  send: function(options) {
    const opts = assign(
      {
        responseType: 'json',
        withCredentials: true,
      },
      options
    );

    return axios(opts);
  },

  /**
   * Factory to make requests with provided basic options, i.e. base URL
   *
   * @param {Object}  options
   * @param {String}  options.baseUrl
   * @param {String}  [options.responseType='json']
   * @param {Boolean} [options.withCredentials=true]
   * @param {Object}  [options.headers]
   * @param {Object}  [options.auth]
   * @param {String}  [options.auth.username]
   * @param {String}  [options.auth.password]
   * @param {String}  [options.xsrfCookieName]
   * @param {String}  [options.xsrfHeaderName]
   */
  createConurrent: function(options) {
    const defaultOptions = {
      responseType: 'json',
      withCredentials: true,
      baseURL: options.baseUrl,
    };
    const requestConfig = assign(defaultOptions, options);
    return axios.create(requestConfig);
  },

  /**
   * Factory to make a requests with built-in protection from concurrent requests.
   *
   * @param {Object}  baseOptions
   * @param {String}  baseOptions.baseUrl
   * @param {String}  [baseOptions.responseType='json']
   * @param {Boolean} [baseOptions.withCredentials=true]
   * @param {Object}  [baseOptions.headers]
   * @param {Object}  [baseOptions.auth]
   * @param {String}  [baseOptions.auth.username]
   * @param {String}  [baseOptions.auth.password]
   * @param {String}  [baseOptions.xsrfCookieName]
   * @param {String}  [baseOptions.xsrfHeaderName]
   * @returns {Function}
   */
  create: function(baseOptions) {
    const requestRegistry = {};
    const defaultOptions = {
      responseType: 'json',
      withCredentials: true,
      baseURL: baseOptions.baseUrl,
    };
    const opts = assign(defaultOptions, baseOptions);
    const _request = axios.create(opts);

    const request = function(requestName, options) {
      if (!requestRegistry[requestName]) {
        requestRegistry[requestName] = _request(options).finally(function() {
          requestRegistry[requestName] = null;
        });
      }
      return requestRegistry[requestName];
    };

    request.register = function(name, req) {
      requestRegistry[name] = req.finally(function() {
        requestRegistry[name] = null;
      });
      return requestRegistry[name];
    };

    request.get = function(name) {
      return requestRegistry[name];
    };

    request.remove = function(name) {
      requestRegistry[name] = null;
    };

    return request;
  },

  /**
   * Factory that creates an instance to make requests with built-in protection from concurrent
   * requests and Bearer authorization. Uses provided function provided as first argument
   * to asynchroniously receive active access_token.
   *
   * @param {Function<Promise>} getToken
   * @param {Object}  baseOptions
   * @param {String}  baseOptions.baseUrl
   * @param {String}  [baseOptions.responseType='json']
   * @param {Boolean} [baseOptions.withCredentials=true]
   * @param {Object}  [baseOptions.headers]
   * @param {Object}  [baseOptions.auth]
   * @param {String}  [baseOptions.auth.username]
   * @param {String}  [baseOptions.auth.password]
   * @param {String}  [baseOptions.xsrfCookieName]
   * @param {String}  [baseOptions.xsrfHeaderName]
   * @returns {Function}
   */
  createWithBearer: function(getToken, baseOptions) {
    const requestRegistry = {};
    const defaultOptions = {
      responseType: 'json',
      withCredentials: true,
      baseURL: baseOptions.baseUrl,
    };
    const _request = axios.create(assign(defaultOptions, baseOptions));
    const buildRequestConfig = function(token, config) {
      const bearer = {
        Authorization: `Bearer ${token}`,
      };
      let requestHeaders = {
        headers: bearer,
      };

      // Extend headers if any provided
      if (config.headers) {
        requestHeaders = assign(bearer, config.headers);
      }

      return assign({}, config, requestHeaders);
    };

    const request = function(requestName, requestConfig) {
      const makeRequest = function(token) {
        return _request.request(buildRequestConfig(token, requestConfig));
      };

      if (!requestRegistry[requestName]) {
        requestRegistry[requestName] = getToken()
          .then(makeRequest)
          .finally(function() {
            requestRegistry[requestName] = null;
          });
      }
      return requestRegistry[requestName];
    };

    request.register = function(name, req) {
      requestRegistry[name] = req.finally(function() {
        requestRegistry[name] = null;
      });
      return requestRegistry[name];
    };

    request.get = function(name) {
      return requestRegistry[name];
    };

    request.remove = function(name) {
      requestRegistry[name] = null;
    };

    return request;
  },
};
